import { Component, DestroyRef, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule, NgbModal, NgbModalOptions, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WorkOrderPaginationRequest } from '../../models/work-order.models';
import { WorkOrderItem } from '../../models/work-orders-overview.models';
import { WorkOrderPriority, WorkOrderPriorityLabels, WorkOrderRequestActionStatus, WorkOrderRequestStatus, WorkOrderStatus, WorkOrderStatusLabels, WorkOrderSourceLabels, WorkOrderServiceTypesLabels } from '../../enums/work-order.enum';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { DateFormatPipe } from '../../../../../_shared/pipes/date-formatter.pipe';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { ConfirmationModalComponent } from '../../../../../_shared/components/modals/confirmation-modal/confirmation-modal.component';
import { ConfirmationOptions } from '../../../../../_shared/models/modals/confirmation-options.model';
import { WorkOrderRequestsOverviewService } from '../../services/work-order-requests-overview.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ScheduleAndAssignModalComponent } from '../modals/schedule-and-assign-modal/schedule-and-assign-modal.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { ToastrService } from 'ngx-toastr';
import { RequestSortColumn, SortDirection } from '../../types/types';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { WorkshopFilterComponent } from '../../../../_shared/components/filters/workshop-filter/workshop-filter.component';
import { AccountFilterData, WorkshopFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { ApproveWorkOrdersRequest, UpdateWorkOrderRequestStatusRequest, WorkOrderApproval } from '../../models/work-order-requests-overview.models';
import { BulkApproveListModalComponent } from './bulk-approve-list-modal/bulk-approve-list-modal.component';
import { PhotosVideosViewerModalComponent } from '../../../../_shared/components/modals/photos-videos-viewer-modal/photos-videos-viewer-modal.component';
import { MakeModelFilterData } from '../../../../_shared/models/make-model.model';
import { MakeAndModelFilterComponent } from '../../../../_shared/components/filters/make-model-filter/make-model-filter.component';
import { FilterService } from '../../../../../_shared/services';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-work-order-requests-overview',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    NgbTooltipModule,
    EmptyStateComponent,
    ErrorStateComponent,
    PaginationComponent,
    SearchComponent,
    WorkshopFilterComponent,
    MakeAndModelFilterComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './work-order-requests-overview.component.html',
  styleUrl: './work-order-requests-overview.component.scss'
})
export class WorkOrderRequestsOverviewComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private modalService = inject(NgbModal);
  private filterService = inject(FilterService);
  private workOrderRequestsService = inject(WorkOrderRequestsOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);

  @Input() currencySymbol: string = 'R';

  accountFilter: AccountFilterData = {} as AccountFilterData;
  accountIdsChanged$: Subject<number[]> = new Subject();

  // Internal data state
  workOrderRequests: WorkOrderItem[] = [];
  isLoading: boolean = true;
  hasError: boolean = false;
  page: number = 1;
  readonly pageSize: number = 7;
  totalPages: number = 0;
  totalCount: number = 0;

  // Internal filter state
  searchTerm: string = '';
  workshopFilter: WorkshopFilterData = { workshops: [] };
  makeModelFilter: MakeModelFilterData = { makeModels: [] };
  sortColumn: RequestSortColumn | null = null;
  sortDirection: SortDirection = null;

  // Selection state
  selectedRequestIds: Set<number> = new Set();
  private allLoadedRequests: WorkOrderItem[] = [];
  private visitedPages: Set<number> = new Set();

  isExporting: boolean = false;

  private readonly cancelSubject = new Subject<void>();
  private readonly STATUS_MESSAGES: Record<WorkOrderRequestActionStatus, string> = {
    [WorkOrderRequestActionStatus.Approve]: 'workOrder.toasts.approved',
    [WorkOrderRequestActionStatus.Reject]: 'workOrder.toasts.rejected',
    [WorkOrderRequestActionStatus.Reset]: 'workOrder.toasts.rejectionReverted',
  };

  // Enums for template
  WorkOrderPriority = WorkOrderPriority;
  WorkOrderStatus = WorkOrderStatus;
  WorkOrderRequestStatus = WorkOrderRequestStatus;
  WorkOrderPriorityLabels = WorkOrderPriorityLabels;
  WorkOrderStatusLabels = WorkOrderStatusLabels;
  WorkOrderSourceLabels = WorkOrderSourceLabels;
  WorkOrderServiceTypeLabels = WorkOrderServiceTypesLabels;

  private dateFormatPipe = new DateFormatPipe();

  hasSelection = signal<boolean>(false);

  get showingCount(): number {
    if (this.totalCount === 0) return 0;
    return Math.min(this.page * this.pageSize, this.totalCount);
  }

  get selectableRows(): WorkOrderItem[] {
    return this.workOrderRequests.filter(r => r.status !== WorkOrderStatus.Rejected);
  }

  private get loadedSelectableCount(): number {
    return this.allLoadedRequests.filter(r => r.status !== WorkOrderStatus.Rejected).length;
  }

  private get hasLoadedAllPages(): boolean {
    return this.totalPages > 0 && this.visitedPages.size >= this.totalPages;
  }

  get isAllSelected(): boolean {
    if (this.selectedRequestIds.size === 0) return false;
    if (!this.hasLoadedAllPages) return false;
    const globalSelectable = this.loadedSelectableCount;
    return globalSelectable > 0 && this.selectedRequestIds.size === globalSelectable;
  }

  get isIndeterminate(): boolean {
    return this.selectedRequestIds.size > 0 && !this.isAllSelected;
  }

  ngOnInit(): void {
    this.filterService.filterUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filter) => {
        const accountIds = filter.accountIds;
        this.accountFilter = { accountIds };
        this.accountIdsChanged$.next(accountIds)
        this.makeModelFilter = { makeModels: [] };
        this.workshopFilter = { workshops: [] };
        this.resetAccumulatedState();
        this.loadData();
      });
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const workshopIds = this.workshopFilter.workshops
        ?.map(w => w.id)
        .filter(id => id > 0) ?? [];

      const vehicleModelIds = this.makeModelFilter.makeModels
        ?.map(m => m.modelId)
        .filter(id => id > 0) ?? [];

      const request: WorkOrderPaginationRequest = {
        accountIds: this.accountFilter.accountIds ?? [],
        page: this.page,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm,
        sortColumn: this.sortColumn ?? undefined,
        sortDirection: this.sortDirection ?? undefined,
        statuses: [WorkOrderStatus.Pending, WorkOrderStatus.Rejected],
        workshopIds: workshopIds.length > 0 ? workshopIds : undefined,
        vehicleModelIds: vehicleModelIds.length > 0 ? vehicleModelIds : undefined,
      };

      this.cancelSubject.next();
      const response = await this.workOrderRequestsService.getWorkOrderRequestsAsync(request, this.cancelSubject);

      this.totalPages = response.data?.totalPages ?? 0;
      this.totalCount = response.data?.totalCount ?? 0;
      this.workOrderRequests = response.data?.results ?? [];

      if (this.workOrderRequests.length === 0 && this.page > 1) {
        this.page = 1;
        this.resetAccumulatedState();
        await this.loadData();
        return;
      }

      const merged = [...this.allLoadedRequests, ...this.workOrderRequests];
      const byId = new Map<number, WorkOrderItem>();
      merged.forEach(r => byId.set(r.workOrderId, r));
      this.allLoadedRequests = Array.from(byId.values());
      this.visitedPages.add(this.page);

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.page = 1;
    this.resetAccumulatedState();
    this.loadData();
  }

  onMakeAndModelChange(filter: MakeModelFilterData): void {
    this.makeModelFilter = filter;
    this.page = 1;
    this.resetAccumulatedState();
    this.loadData();
  }

  onWorkshopFilterChange(filter: WorkshopFilterData): void {
    this.workshopFilter = filter;
    this.page = 1;
    this.resetAccumulatedState();
    this.loadData();
  }

  formatDate(date: Date | string): string {
    return this.dateFormatPipe.transform(date);
  }

  toggleSelectAll(): void {
    const selectable = this.selectableRows;
    if (selectable.length === 0) return;

    const isCurrentPageFullySelected = selectable.every(r => this.selectedRequestIds.has(r.workOrderId));
    if (isCurrentPageFullySelected) {
      selectable.forEach(r => this.selectedRequestIds.delete(r.workOrderId));
    } else {
      selectable.forEach(r => this.selectedRequestIds.add(r.workOrderId));
    }

    this.hasSelection.set(this.selectedRequestIds.size > 1);
  }

  toggleWorkOrderSelection(requestId: number): void {
    if (this.selectedRequestIds.has(requestId)) {
      this.selectedRequestIds.delete(requestId);
    } else {
      this.selectedRequestIds.add(requestId);
    }

    this.hasSelection.set(this.selectedRequestIds.size > 1);
  }

  isSelected(requestId: number): boolean {
    return this.selectedRequestIds.has(requestId);
  }

  clearSelection(): void {
    this.selectedRequestIds.clear();
    this.allLoadedRequests = [];
    this.visitedPages.clear();
    this.hasSelection.set(false);
  }

  private resetAccumulatedState(): void {
    this.selectedRequestIds.clear();
    this.allLoadedRequests = [];
    this.visitedPages.clear();
    this.hasSelection.set(false);
  }

  formatCost(cost: number): string {
    return `${this.currencySymbol}${cost.toLocaleString()}`;
  }

  getPriorityClass(priority: WorkOrderPriority): string {
    switch (priority) {
      case WorkOrderPriority.Low:
        return 'priority-low';
      case WorkOrderPriority.Medium:
        return 'priority-medium';
      case WorkOrderPriority.High:
        return 'priority-high';
      default:
        return '';
    }
  }

  getWorkOrderStatusClass(status: WorkOrderStatus): string {
    switch (status) {
      case WorkOrderStatus.Pending:
        return 'status-requested';
      case WorkOrderStatus.Rejected:
        return 'status-rejected';
      default:
        return '';
    }
  }

  bulkApprove(): void {
    if (!this.hasSelection()) return;

    const selectedWorkOrders = this.allLoadedRequests.filter(r => this.selectedRequestIds.has(r.workOrderId));

    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'lg',
      windowClass: 'bulk-approve-modal-window'
    };

    const modalRef = this.modalService.open(BulkApproveListModalComponent, modalOptions);
    modalRef.componentInstance.workOrders = selectedWorkOrders;
    modalRef.componentInstance.currencySymbol = this.currencySymbol;

    modalRef.componentInstance.itemRemoved.subscribe((workOrderId: number) => {
      this.selectedRequestIds.delete(workOrderId);
      this.hasSelection.set(this.selectedRequestIds.size > 1);
    });

    modalRef.componentInstance.onSubmit = async (workOrders: WorkOrderApproval[]): Promise<void> => {
      try {
        const request: ApproveWorkOrdersRequest = { workOrders };
        await this.workOrderRequestsService.approveWorkOrderRequestsAsync(request, this.cancelSubject);
        this.toastr.success(this.transloco.translate(
          workOrders.length === 1 ? 'workOrder.toasts.bulkApprovedOne' : 'workOrder.toasts.bulkApprovedMany',
          { count: workOrders.length }));
        this.clearSelection();
        modalRef.close();
        this.loadData();
      } catch (error: any) {
        this.errorHandlerService.handleError(error, {
          showToast: true
        });
      }
    };
  }

  getPhotoCountLabel(request: WorkOrderItem): string {
    const count = request.faultImages?.length ?? 0;
    const noun = this.transloco.translate(count === 1 ? 'workOrder.table.photo' : 'workOrder.table.photos');
    return `${count} ${noun}`;
  }

  onViewPhotos(request: WorkOrderItem): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      windowClass: 'photos-videos-viewer-modal'
    };

    const modalRef = this.modalService.open(PhotosVideosViewerModalComponent, modalOptions);
    modalRef.componentInstance.title = this.transloco.translate('workOrder.photos.title');
    modalRef.componentInstance.contextLabel = request.workOrderNumber;
    modalRef.componentInstance.items = request.faultImages ?? [];
    modalRef.componentInstance.downloadLinkResolver = async (item: { reference: string }): Promise<string | null> => {
      try {
        const response = await this.workOrderRequestsService.getWorkOrderFaultImageDownloadLinkAsync(
          request.workOrderNumber, item.reference, this.cancelSubject);
        return response.data ?? null;
      } catch {
        return null;
      }
    };
  }

  onApprove(request: WorkOrderItem): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'schedule-and-assign-modal'
    };

    const modalRef = this.modalService.open(ScheduleAndAssignModalComponent, modalOptions);
    modalRef.componentInstance.workOrderData = request;
    modalRef.componentInstance.workOrderId = request.workOrderId;
    modalRef.componentInstance.approveMode = true;
    modalRef.componentInstance.onApprove = async (scheduledDate: Date, technicianIds: number[]) => {
      try {
        if (technicianIds.length === 0) return;

        const worOrderApproval: ApproveWorkOrdersRequest = {
          workOrders: [{ workOrderId: request.workOrderId, scheduledDate, technicianIds }]
        };

        await this.workOrderRequestsService.approveWorkOrderRequestsAsync(worOrderApproval, this.cancelSubject);
        this.toastr.success(this.transloco.translate('workOrder.toasts.approved'));
        modalRef.close();
        this.loadData();
      } catch (error: any) {
        this.errorHandlerService.handleError(error, {
          showToast: true,
        });
      }
    };
  }

  private async approveWorkOrder(workOrderId: number, scheduledDate: Date, technicianId: number): Promise<boolean> {
    var response = false;


    return response;
  }

  onRevertRejection(request: WorkOrderItem): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static'
    };

    const modalRef = this.modalService.open(ConfirmationModalComponent, modalOptions);
    modalRef.componentInstance.data = {
      title: this.transloco.translate('workOrder.confirmations.revert.title'),
      message: this.transloco.translate('workOrder.confirmations.revert.message', { workOrderNumber: request.workOrderNumber }),
      confirmText: this.transloco.translate('workOrder.confirmations.revert.confirmText'),
      declineText: this.transloco.translate('workOrder.confirmations.revert.declineText'),
      onConfirm: async () => this.updateWorkOrderRequest(request.workOrderId, WorkOrderRequestActionStatus.Reset),
      closeOnSuccess: true
    } as ConfirmationOptions;
  }

  onReject(request: WorkOrderItem): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static'
    };

    const modalRef = this.modalService.open(ConfirmationModalComponent, modalOptions);
    modalRef.componentInstance.data = {
      title: this.transloco.translate('workOrder.confirmations.reject.title'),
      message: this.transloco.translate('workOrder.confirmations.reject.message', { workOrderNumber: request.workOrderNumber }),
      confirmText: this.transloco.translate('workOrder.confirmations.reject.confirmText'),
      declineText: this.transloco.translate('workOrder.confirmations.reject.declineText'),
      onConfirm: async () => this.updateWorkOrderRequest(request.workOrderId, WorkOrderRequestActionStatus.Reject),
      closeOnSuccess: true
    } as ConfirmationOptions;
  }

  private async updateWorkOrderRequest(workOrderId: number, status: WorkOrderRequestActionStatus): Promise<void> {
    try {
      const request: UpdateWorkOrderRequestStatusRequest = {
        workOrderId: workOrderId,
        status: status
      };

      await this.workOrderRequestsService.updateWorkOrderRequestStatusAsync(request, this.cancelSubject);

      const messageKey = this.STATUS_MESSAGES[status];
      if (messageKey) {
        this.toastr.success(this.transloco.translate(messageKey));
      }

      this.loadData();
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    }
  }

  onPageChange(page: number): void {
    this.page = page;
    this.loadData();
  }

  onSort(column: RequestSortColumn): void {
    const nextDirection = this.getNextSortDirection(column);
    this.sortColumn = nextDirection ? column : null;
    this.sortDirection = nextDirection;
    this.page = 1;
    this.resetAccumulatedState();
    this.loadData();
  }

  private getNextSortDirection(column: RequestSortColumn): SortDirection {
    if (this.sortColumn !== column || !this.sortDirection) {
      return 'asc';
    }

    return this.sortDirection === 'asc' ? 'desc' : null;
  }

  async onExport(format: 'excel' | 'csv'): Promise<void> {
    try {
      this.isExporting = true;
      const request: WorkOrderPaginationRequest = {
        accountIds: this.accountFilter.accountIds,
        page: 1,
        pageSize: this.totalCount
      };

      const blob = await this.workOrderRequestsService.exportWorkOrderRequestsAsync(request, this.cancelSubject);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `WorkOrderRequests_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      this.toastr.success(this.transloco.translate('workOrder.toasts.exportCompleted'));
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isExporting = false;
    }
  }
}